import { defineStore } from "pinia";
import cargoPropertiesApi from "@/services/api/cargoPropertiesApi";
import { getCompanyId } from "@/utils/userUtils";

export const useCargoPropertyStore = defineStore(
  "cargoPropertyStore",
  {
    state: () => ({
      items: [],
      companyId: getCompanyId(),
      isLoading: false,
      error: null,
      lastFetchParams: {},

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
    }),

    getters: {
      activeItems: (state) =>
        state.items.filter((i) => i.enabled !== false),
    },

    actions: {
      async getItemsRequest(params = {}) {
        this.isLoading = true;
        this.lastFetchParams = params;
        try {
          const response = await cargoPropertiesApi.index(
            this.companyId,
            params
          );
          this.items = response.data.result || response.data || [];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error(
            "Ошибка при получении характеристик груза:",
            error
          );
        } finally {
          this.isLoading = false;
        }
      },

      async loadMoreItems() {
        if (
          this.isLoading ||
          this.pagination.page >= this.pagination.pages
        )
          return;

        this.isLoading = true;
        try {
          const response = await cargoPropertiesApi.index(
            this.companyId,
            {
              ...this.lastFetchParams,
              page: this.pagination.page + 1,
            }
          );

          this.items = [
            ...this.items,
            ...(response.data.result || response.data || []),
          ];

          const meta = response.data.pagination_meta;
          if (meta) {
            this.pagination = {
              page: meta.page,
              pages: meta.pages,
              series: meta.series,
              next: meta.next,
              prev: meta.prev,
              count: meta.count,
              limit: meta.limit,
            };
          }
        } catch (error) {
          this.error = error;
          console.error(
            "Ошибка при подгрузке характеристик груза:",
            error
          );
        } finally {
          this.isLoading = false;
        }
      },

      async createItem(payload) {
        this.isLoading = true;
        try {
          const response = await cargoPropertiesApi.create(
            this.companyId,
            payload
          );
          await this.getItemsRequest(this.lastFetchParams);
          return response.data.result || response.data || {};
        } catch (error) {
          console.error(
            "Ошибка при создании характеристики груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async updateItem(id, payload) {
        this.isLoading = true;
        try {
          await cargoPropertiesApi.update(
            this.companyId,
            id,
            payload
          );
          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error(
            "Ошибка при обновлении характеристики груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemEnable(id) {
        this.isLoading = true;
        try {
          await cargoPropertiesApi.enable(this.companyId, id);
          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error(
            "Ошибка при включении характеристики груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },

      async itemDisable(id) {
        this.isLoading = true;
        try {
          await cargoPropertiesApi.disable(this.companyId, id);
          await this.getItemsRequest(this.lastFetchParams);
        } catch (error) {
          console.error(
            "Ошибка при отключении характеристики груза:",
            error
          );
          throw error;
        } finally {
          this.isLoading = false;
        }
      },
    },
  }
);
